import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { HandlePreview, WaitlistSubmission } from "../types";

/**
 * Promotion is the one screen where a silent mistake is costly: the allowlist
 * doc id must equal what inbound Sendblue traffic normalizes to, or the person
 * is "approved" and then ignored forever with no error anywhere.
 *
 * So the handle is always resolved by the SERVER (via /preview-handle) and shown
 * to the admin before anything is written. This component deliberately contains
 * no phone-parsing logic of its own — a second implementation here would
 * eventually disagree with the one that matters.
 */
export function PromoteDialog({
  submission,
  onClose,
  onApproved,
}: {
  submission: WaitlistSubmission;
  onClose: () => void;
  onApproved: (id: string) => void;
}) {
  const [raw, setRaw] = useState(submission.phone ?? "");
  const [preview, setPreview] = useState<HandlePreview | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Re-resolve as the admin edits, debounced.
  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(() => {
      api
        .previewHandle(submission.id, raw)
        .then((p) => !cancelled && setPreview(p))
        .catch((e) => !cancelled && setError(e.message))
        .finally(() => !cancelled && setChecking(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [raw, submission.id]);

  const isAndroid = (submission.mobileOs ?? "").toLowerCase() === "android";
  const canApprove = !!preview?.valid && !busy && !isAndroid;

  async function approve() {
    if (!preview?.valid) return;
    setBusy(true);
    setError("");
    try {
      // Send the text, not the resolved id — same as the allowlist add form.
      // Resolution is not idempotent: a resolved id is bare digits with no '+',
      // and feeding that back to the server made it read `819039249945` as a
      // NANP number and reject the promotion. NANP ids happened to survive the
      // round trip, so this only ever broke numbers outside +1.
      await api.approve(submission.id, raw.trim());
      onApproved(submission.id);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="card modal"
        role="dialog"
        aria-modal="true"
        aria-label="Promote to allowlist"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Add to allowlist</h2>
        {(submission.status === "approved_pending_activation" ||
          submission.status === "activation_expired") && (
          <p className="muted">
            This manual promotion grants permanent access immediately. It does
            not use the seven-day activation window.
          </p>
        )}
        <dl className="kv">
          <dt>Email</dt>
          <dd>{submission.email || "—"}</dd>
          <dt>Phone as typed</dt>
          <dd>
            <code>{submission.phone || "—"}</code>
          </dd>
          <dt>Device</dt>
          <dd>{submission.mobileOs || "—"}</dd>
        </dl>

        {isAndroid && (
          <p className="error" role="alert">
            This signup is Android. The allowlist gates iMessage, so promotion is
            blocked.
          </p>
        )}

        <label className="field">
          <span>Allowlist handle</span>
          <input
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            disabled={isAndroid}
          />
        </label>

        <div className="resolved" aria-live="polite">
          {checking && <span className="muted">Checking…</span>}
          {!checking && preview?.valid && (
            <>
              <span className="ok">Will be written as</span>{" "}
              <code>{preview.handle}</code>
              {preview.already_allowlisted && (
                <span className="muted"> · already in the allowlist</span>
              )}
            </>
          )}
          {!checking && preview && !preview.valid && (
            <span className="error">{preview.reason}</span>
          )}
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="primary" onClick={approve} disabled={!canApprove}>
            {busy ? "Adding…" : "Add to allowlist"}
          </button>
        </div>
      </div>
    </div>
  );
}
